import { useMemo, useRef, useState } from "react";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Form, Input, Alert, Flex, Typography, Divider } from "antd";
import {
  building_number_regexp,
  apartment_number_regexp,
} from "helpers/regexp";
import useCreate from "hooks/useCreate";

const nextLetter = (str) => {
  const lastChar = str.slice(-1);
  if (/[a-y]$/.test(lastChar)) {
    return str.slice(0, -1) + String.fromCharCode(lastChar.charCodeAt(0) + 1);
  } else if (lastChar === "z") {
    return str + "a";
  } else {
    return str + "a";
  }
};

const IssueGenerator = ({ issue_group_id, handleClose, refetch }) => {
  const ref = useRef();
  const [input_from, setInputFrom] = useState("");
  const [input_to, setInputTo] = useState("");

  const { is_saving: is_generating, create: generateSubIssues } = useCreate({
    path: `/issue_groups/${issue_group_id}/generate_sub_issues`,
    onSuccess: refetch,
    success_message: "Wygenerowano podzgłoszenia",
  });

  const onFinish = async (values) => {
    const apartment_numbers = values.apartment_numbers
      .map(({ number, sub_numbers }) => [number, ...sub_numbers])
      .flat();
    await generateSubIssues({ apartment_numbers });
    handleClose();
  };

  const generator_data = useMemo(() => {
    let is_disabled = false;
    const errors = [];
    if (!input_from || !input_to) {
      is_disabled = true;
      return {
        errors,
        is_disabled,
      };
    }

    if (Number(input_from) < 1) {
      is_disabled = true;
      errors.push('Wartość "Od" nie może być mniejsza od 0');
    }
    if (Number(input_to) < 1) {
      is_disabled = true;
      errors.push('Wartość "Do" nie może być mniejsza od 0');
    }
    if (Number(input_to) <= Number(input_from)) {
      is_disabled = true;
      errors.push('Wartość "Do" nie może być mniejsza lub równa wartości "Od"');
    }

    return {
      errors,
      is_disabled,
    };
  }, [input_from, input_to]);

  return (
    <>
      <Divider />
      <Form name="dynamic_form_item" onFinish={onFinish} ref={ref}>
        {({ apartment_numbers }) => (
          <>
            <Alert
              message="Zgłosznia zostaną wygenerowane na podstawie zgłoszenia na którym jesteś."
              banner
              type="info"
              style={{ marginBottom: 20 }}
            />
            <Form.List
              name="apartment_numbers"
              rules={[
                {
                  validator: async (_, apartment_numbers) => {
                    if (!apartment_numbers || apartment_numbers.length < 1) {
                      return Promise.reject(
                        new Error("Wymagane podanie minimum 1 pozycji")
                      );
                    }
                    if (apartment_numbers?.length > 50) {
                      return Promise.reject(
                        new Error("Maksymalnie 50 pozycji")
                      );
                    }
                  },
                },
              ]}
            >
              {(fields, { add, remove }, { errors }) => {
                const reset = () => {
                  setInputFrom("");
                  setInputTo("");
                  ref.current.setFieldValue("apartment_numbers", []);
                };

                const handleGenerateFields = () => {
                  for (
                    let index = Number(input_from);
                    index <= input_to;
                    index++
                  ) {
                    add({ number: String(index), sub_numbers: [] });
                  }
                  setInputFrom("");
                  setInputTo("");
                };

                const nextNumber = () => {
                  if (!fields.length) {
                    return "";
                  }

                  const last_field = fields[fields.length - 1];

                  const value = ref.current.getFieldValue([
                    "apartment_numbers",
                    last_field.name,
                    "number",
                  ]);
                  const value_number = value.replace(/\D+/g, "");
                  if (value_number) {
                    return String(Number(value_number) + 1);
                  }
                  return "";
                };

                const handleAddSubNumber = (field) => {
                  const path = ["apartment_numbers", field.name, "sub_numbers"];
                  const main_number = ref.current.getFieldValue([
                    "apartment_numbers",
                    field.name,
                    "number",
                  ]);
                  const current_value = ref.current.getFieldValue(path) || [];
                  const last_current_value =
                    current_value?.length > 0
                      ? current_value[current_value.length - 1]
                      : main_number;

                  ref.current.setFieldValue(path, [
                    ...current_value,
                    nextLetter(last_current_value),
                  ]);
                };

                return (
                  <>
                    <p style={{ marginBottom: 5 }}>
                      Wygeneruj numery mieszkań w kolejności
                    </p>
                    <Flex vertical gap={5} className="m-b-20">
                      <div style={{ maxWidth: 400 }}>
                        <Flex align="center" gap={10}>
                          <Input
                            min={1}
                            placeholder="Od"
                            type="number"
                            value={input_from}
                            onChange={({ target: { value } }) =>
                              setInputFrom(value)
                            }
                          />
                          <span>-</span>
                          <Input
                            min={1}
                            placeholder="Do"
                            type="number"
                            value={input_to}
                            onChange={({ target: { value } }) =>
                              setInputTo(value)
                            }
                          />
                          <Button
                            type="primary"
                            onClick={handleGenerateFields}
                            disabled={generator_data.is_disabled}
                          >
                            Wygeneruj
                          </Button>
                          <Button
                            danger
                            onClick={reset}
                            disabled={!fields.length}
                          >
                            Resetuj
                          </Button>
                        </Flex>
                        {generator_data?.errors?.length > 0 && (
                          <div className="m-t-10">
                            <Typography.Text type="danger">
                              <Form.ErrorList errors={generator_data.errors} />
                            </Typography.Text>
                          </div>
                        )}
                      </div>
                    </Flex>
                    <>
                      {fields.map((field, index) => (
                        <div key={field.key}>
                          <Form.Item
                            layout="vertical"
                            label={`Numer mieszkania #${index + 1}`}
                            required
                          >
                            <Flex gap={20}>
                              <Form.Item
                                {...field}
                                name={[field.name, "number"]}
                                rules={[
                                  {
                                    required: true,
                                    message: "Numer mieszkania jest wymagany",
                                  },
                                  {
                                    pattern: building_number_regexp,
                                    message: "Nieprawidłowy numer mieszkania",
                                  },
                                ]}
                                noStyle
                              >
                                <Input maxLength={10} />
                              </Form.Item>
                              <Flex gap={8}>
                                <Button
                                  htmlType="button"
                                  onClick={() => handleAddSubNumber(field)}
                                  icon={<PlusOutlined />}
                                />
                                <Button
                                  danger
                                  htmlType="button"
                                  onClick={() => remove(field.name)}
                                  icon={<DeleteOutlined />}
                                />
                              </Flex>
                            </Flex>
                          </Form.Item>
                          <div className="m-l-20">
                            <Form.List name={[field.name, "sub_numbers"]}>
                              {(subFields, subOpt) =>
                                subFields.map((subField) => (
                                  <Form.Item key={subField.key}>
                                    <Flex gap={20}>
                                      <Form.Item
                                        {...subField}
                                        rules={[
                                          {
                                            required: true,
                                            message:
                                              "Numer mieszkania jest wymagany",
                                          },
                                          {
                                            pattern: apartment_number_regexp,
                                            message:
                                              "Nieprawidłowy numer mieszkania",
                                          },
                                        ]}
                                        noStyle
                                      >
                                        <Input maxLength={10} />
                                      </Form.Item>

                                      <Button
                                        danger
                                        htmlType="button"
                                        onClick={() =>
                                          subOpt.remove(subField.name)
                                        }
                                        icon={<DeleteOutlined />}
                                      />
                                    </Flex>
                                  </Form.Item>
                                ))
                              }
                            </Form.List>
                          </div>
                        </div>
                      ))}
                    </>
                    {errors?.length > 0 && (
                      <div className="m-b-20">
                        <Typography.Text type="danger">
                          <Form.ErrorList errors={errors} />
                        </Typography.Text>
                      </div>
                    )}
                    <div className="m-t-30 m-b-30" gap={8}>
                      <Button
                        onClick={() =>
                          add({ number: nextNumber(), sub_numbers: [] })
                        }
                        icon={<PlusOutlined />}
                      >
                        Dodaj pozycję
                      </Button>
                    </div>
                  </>
                );
              }}
            </Form.List>
            <Flex justify="flex-end" gap={8}>
              <Button key="back" onClick={handleClose}>
                Anuluj
              </Button>
              <Button
                key="submit"
                type="primary"
                loading={is_generating}
                disabled={!apartment_numbers?.length}
                htmlType="submit"
              >
                Utworz
              </Button>
            </Flex>
          </>
        )}
      </Form>
    </>
  );
};

export default IssueGenerator;
